import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Calendar, Clock, Phone, Mail, User, CheckCircle, Star, Award, MapPin, Info, ArrowRight, Send, Gift, Shield, Heart, Utensils } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import ModernSelect from '../components/ModernSelect';
import Footer from '../components/Footer';

interface ReservationFormData {
    name: string;
    email: string;
    phone: string;
    date: string;
    time: string;
    guests: number;
    message: string;
}

const Reservation = () => {
    const { t } = useLanguage();
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm<ReservationFormData>();

    const timeSlots = [
        '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
        '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'
    ];

    const onSubmit = async (data: ReservationFormData) => {
        setIsLoading(true);
        try {
            await axios.post('/api/v1/reservations', {
                name: data.name,
                email: data.email,
                phone: data.phone,
                date: new Date(data.date).toISOString(),
                time: data.time,
                guests: parseInt(data.guests as any, 10),
                message: data.message,
            });

            setIsSubmitted(true);
            reset();
            toast.success(t('booking.success'));
        } catch (error: any) {
            console.error('Reservation error:', error);
            toast.error(error.response?.data?.error || t('booking.error'));
        } finally {
            setIsLoading(false);
        }
    };

    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    if (isSubmitted) {
        return (
            <div className="pt-16 lg:pt-20">
                {/* Enhanced Success Page */}
                <section className="relative min-h-screen section-padding bg-gradient-to-br from-green-500 via-primary-600 to-accent-600 overflow-hidden">
                    {/* Background Decorations */}
                    <div className="absolute inset-0">
                        <div className="absolute top-20 left-10 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
                        <div className="absolute bottom-20 right-10 w-80 h-80 bg-white/5 rounded-full blur-2xl"></div>
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/3 rounded-full blur-2xl"></div>

                        {/* Floating Elements */}
                        <motion.div
                            animate={{ y: [-20, 20, -20], rotate: [0, 5, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute top-32 right-32 w-12 h-12 bg-white/10 rounded-full blur-sm"
                        ></motion.div>
                        <motion.div
                            animate={{ y: [20, -20, 20], rotate: [0, -5, 0] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute bottom-32 left-32 w-8 h-8 bg-white/10 rounded-full blur-sm"
                        ></motion.div>
                    </div>

                    <div className="relative container mx-auto max-w-4xl container-padding text-center flex items-center justify-center min-h-screen">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, y: 50 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            {/* Success Icon */}
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.3, type: "spring", stiffness: 200 }}
                                className="relative w-32 h-32 mx-auto mb-8"
                            >
                                <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center relative">
                                    <CheckCircle className="w-20 h-20 text-white" />
                                    <motion.div
                                        animate={{ scale: [1, 1.2, 1] }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                        className="absolute inset-0 border-4 border-white/30 rounded-full"
                                    ></motion.div>
                                </div>
                            </motion.div>

                            {/* Success Message */}
                            <motion.h1
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                                className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 text-white leading-tight"
                            >
                                {t('reservation.confirmed.title')}
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.7 }}
                                className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed font-light"
                            >
                                {t('reservation.confirmed.thankYou')}
                            </motion.p>

                            {/* Confirmation Details */}
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.9 }}
                                className="bg-white/15 rounded-3xl p-8 mb-12 max-w-2xl mx-auto border border-white/20"
                            >
                                <div className="flex items-center justify-center space-x-3 mb-6">
                                    <Shield className="w-6 h-6 text-white" />
                                    <h3 className="text-xl font-bold text-white">{t('reservation.confirmed.details')}</h3>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                    <div className="text-center">
                                        <Calendar className="w-8 h-8 text-yellow-300 mx-auto mb-2" />
                                        <p className="text-white/80 text-sm">{t('reservation.confirmed.confirmationId')}</p>
                                        <p className="text-white font-semibold">#RES{Math.floor(Math.random() * 10000)}</p>
                                    </div>
                                    <div className="text-center">
                                        <Clock className="w-8 h-8 text-yellow-300 mx-auto mb-2" />
                                        <p className="text-white/80 text-sm">{t('reservation.confirmed.responseTime')}</p>
                                        <p className="text-white font-semibold">{t('reservation.confirmed.within2Hours')}</p>
                                    </div>
                                    <div className="text-center">
                                        <Star className="w-8 h-8 text-yellow-300 mx-auto mb-2" />
                                        <p className="text-white/80 text-sm">{t('reservation.confirmed.status')}</p>
                                        <p className="text-white font-semibold">{t('reservation.confirmed.confirmed')}</p>
                                    </div>
                                </div>

                                <div className="bg-white/10 rounded-2xl p-4">
                                    <p className="text-white/90 text-sm">
                                        {t('reservation.confirmed.emailSent')}
                                    </p>
                                </div>
                            </motion.div>

                            {/* Action Buttons */}
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 1.1 }}
                                className="flex flex-col sm:flex-row gap-6 justify-center max-w-lg mx-auto"
                            >
                                <motion.button
                                    onClick={() => setIsSubmitted(false)}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="group bg-white text-gray-900 hover:bg-gray-100 font-semibold py-4 px-8 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center justify-center space-x-3"
                                >
                                    <Calendar className="w-5 h-5 text-primary-600" />
                                    <span>{t('reservation.confirmed.makeAnother')}</span>
                                </motion.button>
                                <motion.a
                                    href="/"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="group bg-transparent border-2 border-white text-white hover:bg-white hover:text-gray-900 font-semibold py-4 px-8 rounded-2xl transition-all duration-300 inline-flex items-center justify-center space-x-3"
                                >
                                    <span>{t('reservation.confirmed.returnHome')}</span>
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                                </motion.a>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="pt-16 lg:pt-20">
            {/* Enhanced Header */}
            <section className="relative section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-2xl"></div>
                    <div className="absolute bottom-20 right-10 w-80 h-80 bg-accent/5 rounded-full blur-2xl"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary/3 rounded-full blur-2xl"></div>

                    {/* Pattern Overlay */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="w-full h-full" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2030/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                        }}></div>
                    </div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding text-center">
                    <motion.div
                        ref={ref}
                        initial={{ opacity: 0, y: 50 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        {/* Badge */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={inView ? { opacity: 1, scale: 1 } : {}}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="inline-flex items-center space-x-2 bg-primary/10 text-primary-400 px-6 py-3 rounded-full text-sm font-medium mb-8"
                        >
                            <Utensils className="w-5 h-5" />
                            <span>{t('reservation.bookYourTable')}</span>
                        </motion.div>

                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 text-white leading-tight">
                            {t('reservation.reserveDiningExperience')}
                        </h1>
                        <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light mb-12">
                            {t('reservation.secureYourTable')}
                        </p>

                        {/* Quick Features */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="flex justify-center items-center space-x-8 flex-wrap gap-4"
                        >
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <CheckCircle className="w-5 h-5 text-green-400" />
                                <span className="text-sm text-gray-300 font-medium">{t('reservation.instantConfirmation')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Star className="w-5 h-5 text-yellow-500" />
                                <span className="text-sm text-gray-300 font-medium">{t('reservation.awardWinning')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Heart className="w-5 h-5 text-red-500" />
                                <span className="text-sm text-gray-300 font-medium">{t('reservation.madeWithLove')}</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Enhanced Reservation Form */}
            <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-32 left-20 w-64 h-64 bg-primary/3 rounded-full blur-xl"></div>
                    <div className="absolute bottom-32 right-20 w-80 h-80 bg-accent/3 rounded-full blur-xl"></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                            {/* Enhanced Form */}
                            <div className="lg:col-span-2">
                                <motion.div
                                    initial={{ opacity: 0, x: -50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.8 }}
                                    viewport={{ once: true }}
                                    className="bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-100 dark:border-gray-700"
                                >
                                    {/* Form Header */}
                                    <div className="mb-10">
                                        <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                                            <Calendar className="w-4 h-4" />
                                            <span>{t('booking.reservationForm')}</span>
                                        </div>
                                        <h2 className="text-2xl md:text-3xl font-serif font-bold mb-4 text-gray-900 dark:text-white">
                                            {t('booking.titlePerfect')}
                                        </h2>
                                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                            {t('booking.description')}
                                        </p>
                                    </div>

                                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                                        {/* Personal Information */}
                                        <div>
                                            <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white flex items-center space-x-2">
                                                <User className="w-5 h-5 text-primary-600" />
                                                <span>{t('reservation.personalInformation')}</span>
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* Name */}
                                                <div>
                                                    <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                        {t('reservation.fullName')} *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        {...register('name', { required: 'Full name is required' })}
                                                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                                        placeholder={t('booking.form.namePlaceholder')}
                                                    />
                                                    {errors.name && (
                                                        <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                            <span>⚠️</span>
                                                            <span>{errors.name.message}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Email */}
                                                <div>
                                                    <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                        {t('reservation.emailAddress')} *
                                                    </label>
                                                    <input
                                                        type="email"
                                                        {...register('email', {
                                                            required: 'Email address is required',
                                                            pattern: {
                                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                                message: 'Please enter a valid email address'
                                                            }
                                                        })}
                                                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                                        placeholder={t('booking.form.emailPlaceholder')}
                                                    />
                                                    {errors.email && (
                                                        <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                            <span>⚠️</span>
                                                            <span>{errors.email.message}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Phone */}
                                                <div>
                                                    <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                        {t('reservation.phoneNumber')} *
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        {...register('phone', { required: 'Phone number is required' })}
                                                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                                        placeholder={t('booking.form.phonePlaceholder')}
                                                    />
                                                    {errors.phone && (
                                                        <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                            <span>⚠️</span>
                                                            <span>{errors.phone.message}</span>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Reservation Details */}
                                        <div>
                                            <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white flex items-center space-x-2">
                                                <Calendar className="w-5 h-5 text-accent-600" />
                                                <span>{t('reservation.reservationDetails')}</span>
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                {/* Date */}
                                                <div>
                                                    <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                        {t('reservation.preferredDate')} *
                                                    </label>
                                                    <input
                                                        type="date"
                                                        {...register('date', { required: 'Date is required' })}
                                                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-300"
                                                        min={getMinDate()}
                                                    />
                                                    {errors.date && (
                                                        <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                            <span>⚠️</span>
                                                            <span>{errors.date.message}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Time */}
                                                <div>
                                                    <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                        {t('reservation.preferredTime')} *
                                                    </label>
                                                    <ModernSelect
                                                        options={timeSlots.map((time) => ({
                                                            value: time,
                                                            label: `${time} ${time < '15:00' ? `(${t('reservation.lunch')})` : `(${t('reservation.dinner')})`}`
                                                        }))}
                                                        value={watch('time')}
                                                        onChange={(value) => setValue('time', value)}
                                                        placeholder={t('reservation.selectTimeSlot')}
                                                    />
                                                    {errors.time && (
                                                        <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                            <span>⚠️</span>
                                                            <span>{errors.time.message}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Number of Guests */}
                                                <div>
                                                    <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                        {t('reservation.numberOfGuests')} *
                                                    </label>
                                                    <ModernSelect
                                                        options={Array.from({ length: 20 }, (_, i) => i + 1).map((num) => ({
                                                            value: num.toString(),
                                                            label: `${num} ${num === 1 ? t('reservation.guest') : t('reservation.guests')}`
                                                        }))}
                                                        value={watch('guests')?.toString()}
                                                        onChange={(value) => setValue('guests', parseInt(value))}
                                                        placeholder={t('reservation.selectPartySize')}
                                                    />
                                                    {errors.guests && (
                                                        <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                            <span>⚠️</span>
                                                            <span>{errors.guests.message}</span>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Special Requests */}
                                        <div>
                                            <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white flex items-center space-x-2">
                                                <Gift className="w-5 h-5 text-secondary-600" />
                                                <span>{t('reservation.specialRequests')}</span>
                                            </h3>
                                            <div>
                                                <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                    {t('reservation.additionalInformation')}
                                                </label>
                                                <textarea
                                                    {...register('message')}
                                                    rows={5}
                                                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300 resize-none"
                                                    placeholder={t('reservation.specialOccasionPlaceholder')}
                                                />
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                    {t('reservation.examples')}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Submit Section */}
                                        <div className="border-t border-gray-200 dark:border-gray-600 pt-8">
                                            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                                                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                                    <span>{t('reservation.confirmWithin2Hours')}</span>
                                                </div>
                                                <motion.button
                                                    type="submit"
                                                    disabled={isLoading}
                                                    whileHover={!isLoading ? { scale: 1.05 } : {}}
                                                    whileTap={!isLoading ? { scale: 0.95 } : {}}
                                                    className="inline-flex items-center space-x-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold px-8 py-4 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                                >
                                                    {isLoading ? (
                                                        <>
                                                            <motion.div
                                                                animate={{ rotate: 360 }}
                                                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                                                            />
                                                            <span>{t('reservation.submitting')}</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Send className="w-5 h-5" />
                                                            <span>{t('reservation.reserveMyTable')}</span>
                                                        </>
                                                    )}
                                                </motion.button>
                                            </div>
                                        </div>
                                    </form>
                                </motion.div>
                            </div>

                            {/* Enhanced Info Sidebar */}
                            <div className="lg:col-span-1">
                                <div className="sticky top-8 space-y-6">
                                    {/* Restaurant Info Card */}
                                    <motion.div
                                        initial={{ opacity: 0, x: 50 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.8 }}
                                        viewport={{ once: true }}
                                        className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700"
                                    >
                                        <div className="flex items-center space-x-3 mb-6">
                                            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center">
                                                <Info className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('reservation.restaurantInfo')}</h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{t('reservation.everythingYouNeed')}</p>
                                            </div>
                                        </div>

                                        <div className="space-y-8">
                                            {/* Opening Hours */}
                                            <div>
                                                <div className="flex items-center space-x-2 mb-4">
                                                    <Clock className="w-5 h-5 text-primary-600" />
                                                    <h4 className="font-bold text-gray-900 dark:text-white">{t('reservation.openingHours')}</h4>
                                                </div>
                                                <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4 space-y-2">
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-medium text-gray-800 dark:text-gray-200">{t('reservation.monday')}</span>
                                                        <span className="text-red-600 font-medium">{t('reservation.closed')}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-medium text-gray-800 dark:text-gray-200">{t('reservation.tuesdayToSunday')}</span>
                                                        <span className="text-green-600 font-medium">11:00 AM - 11:00 PM</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Contact Info */}
                                            <div>
                                                <div className="flex items-center space-x-2 mb-4">
                                                    <Phone className="w-5 h-5 text-accent-600" />
                                                    <h4 className="font-bold text-gray-900 dark:text-white">{t('reservation.contactDetails')}</h4>
                                                </div>
                                                <div className="space-y-3">
                                                    <a
                                                        href="tel:+243971566525"
                                                        className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors group"
                                                    >
                                                        <Phone className="w-4 h-4 text-primary-600 group-hover:scale-110 transition-transform" />
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">+243 971 566 525</p>
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">{t('reservation.callUsDirectly')}</p>
                                                        </div>
                                                    </a>
                                                    <a
                                                        href="mailto:contact@ilrasentino.com"
                                                        className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors group"
                                                    >
                                                        <Mail className="w-4 h-4 text-primary-600 group-hover:scale-110 transition-transform" />
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">contact@ilrasentino.com</p>
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">{t('reservation.sendUsEmail')}</p>
                                                        </div>
                                                    </a>
                                                </div>
                                            </div>

                                            {/* Location */}
                                            <div>
                                                <div className="flex items-center space-x-2 mb-4">
                                                    <MapPin className="w-5 h-5 text-secondary-600" />
                                                    <h4 className="font-bold text-gray-900 dark:text-white">{t('reservation.ourLocation')}</h4>
                                                </div>
                                                <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4">
                                                    <p className="text-sm text-gray-800 dark:text-gray-200 font-medium">av. Lyn Lusi 058</p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">Goma</p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">Congo-Kinshasa</p>
                                                    <div className="mt-3 flex items-center space-x-2 text-xs text-primary-600">
                                                        <span>🚗</span>
                                                        <span>{t('reservation.freeParkingAvailable')}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Important Notes */}
                                            <div>
                                                <div className="flex items-center space-x-2 mb-4">
                                                    <Shield className="w-5 h-5 text-yellow-600" />
                                                    <h4 className="font-bold text-gray-900 dark:text-white">{t('reservation.importantNotes')}</h4>
                                                </div>
                                                <div className="space-y-3">
                                                    {[
                                                        { icon: '⏰', text: t('reservation.reservationsHeld') },
                                                        { icon: '👥', text: t('reservation.groupsAdvanceNotice') },
                                                        { icon: '🥗', text: t('reservation.dietaryRequirements') },
                                                        { icon: '✅', text: t('reservation.confirmationWithin2Hours') }
                                                    ].map((note, index) => (
                                                        <div key={index} className="flex items-start space-x-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl">
                                                            <span className="text-sm">{note.icon}</span>
                                                            <p className="text-sm text-gray-700 dark:text-gray-300">{note.text}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Special Offers Card */}
                                    <motion.div
                                        initial={{ opacity: 0, x: 50 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.8, delay: 0.2 }}
                                        viewport={{ once: true }}
                                        className="bg-gradient-to-br from-primary-500 to-accent-600 rounded-3xl p-6 text-white"
                                    >
                                        <div className="flex items-center space-x-3 mb-4">
                                            <Award className="w-8 h-8 text-yellow-300" />
                                            <div>
                                                <h4 className="font-bold text-lg">{t('reservation.specialOffers')}</h4>
                                                <p className="text-sm text-white/80">{t('reservation.limitedTimePromotions')}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="bg-white/10 rounded-xl p-4">
                                                <h5 className="font-semibold mb-1">{t('reservation.anniversarySpecial')}</h5>
                                                <p className="text-sm text-white/90">{t('reservation.complimentaryDessert')}</p>
                                            </div>
                                            <div className="bg-white/10 rounded-xl p-4">
                                                <h5 className="font-semibold mb-1">{t('reservation.winePairing')}</h5>
                                                <p className="text-sm text-white/90">{t('reservation.expertSommelier')}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Reservation;
